import { NON_AUTHORITY, type NonAuthority } from './authority';
import type { FrontendCondition } from './condition';
import { assertNever } from './exhaustive';
import type { Freshness } from './freshness';
import {
  Presence,
  type PresenceAvailable,
  type PresenceUnavailable,
  type PresenceUnknown,
} from './presence';

/**
 * The read-model observation — the single carrier of a read result in the UI.
 *
 * WHAT IT IS
 *   The pairing of a presence, a freshness and a fixed authority, over an opaque
 *   payload `TValue` supplied by the typed consumer layer. `TValue` is a type
 *   parameter on purpose: this package is the state algebra, not the contract
 *   mirror, and it must not know the eight adopted Capability Definition fields.
 *
 * WHAT IT STRUCTURALLY PREVENTS
 *
 *   1. `AVAILABLE` REQUIRES DATA; ABSENT DATA CARRIES NONE.
 *      The frozen contract's own compact constructor states the rule:
 *        "AVAILABLE requires a matching definition snapshot" and
 *        "absent or unavailable data must not carry a snapshot".
 *      Here the value is `TValue` ONLY on the available variant and `null` on the
 *      others, so an observation cannot carry a payload unless its presence is
 *      AVAILABLE. This mirrors FE-1 Stage Work Item §6.5 at the type level
 *      instead of restating it as a convention.
 *
 *   2. A FRONTEND CONDITION CANNOT BE READ AS A CONTRACT OUTCOME.
 *      The frontend-condition variant has NO `presence` and NO `freshness` field.
 *      Code physically cannot read a presence off a loading observation, so
 *      `LOADING` cannot be silently presented as `UNKNOWN` (FE-1 Stage Work Item
 *      §7.5) — the field does not exist.
 *
 *   3. AUTHORITY IS FIXED ON EVERY VARIANT.
 *      `authority: NonAuthority` is required and `NonAuthority` has exactly one
 *      value, so no observation can be built with elevated authority and none can
 *      be built without an authority at all (`Read Model != Authority`).
 *
 *   4. NO SUCCESS PREDICATE EXISTS.
 *      There is deliberately no `isSuccess`, `isOk`, `isReady` or `succeeded`.
 *      `AVAILABLE` means snapshot data was retrieved; it does NOT mean the
 *      capability is operationally available, ready, assigned or committed
 *      (FE-1 Stage Work Item §6.4). A generic success predicate is exactly how
 *      that over-claim would enter the codebase, so there is none, and no
 *      variant may be added that would give one a meaning.
 *
 * MATCHING IS TOTAL AND COLLAPSE-FREE. Use `matchObservation`; it requires a
 * handler for every variant, so a new variant becomes a compile error at every
 * call site rather than a silent fallthrough into a default bucket.
 */

/** A contract outcome: the read plane resolved to AVAILABLE and returned data. */
export interface AvailableObservation<TValue> {
  readonly presence: PresenceAvailable;
  readonly freshness: Freshness;
  readonly authority: NonAuthority;
  readonly value: TValue;
}

/** A contract outcome: the read plane resolved to UNKNOWN. Carries no data. */
export interface UnknownObservation {
  readonly presence: PresenceUnknown;
  readonly freshness: Freshness;
  readonly authority: NonAuthority;
  readonly value: null;
}

/** A contract outcome: the reader could not supply a trustworthy response. */
export interface UnavailableObservation {
  readonly presence: PresenceUnavailable;
  readonly freshness: Freshness;
  readonly authority: NonAuthority;
  readonly value: null;
}

/** A contract outcome. */
export type ContractObservation<TValue> =
  AvailableObservation<TValue> | UnknownObservation | UnavailableObservation;

/**
 * A frontend condition. Carries NO presence and NO freshness by construction —
 * see prevention (2) above.
 */
export interface FrontendConditionObservation {
  readonly condition: FrontendCondition;
  readonly authority: NonAuthority;
}

/** Everything the UI may hold about a read plane. */
export type ReadModelObservation<TValue> =
  ContractObservation<TValue> | FrontendConditionObservation;

/**
 * Total match over an observation.
 *
 * All four handlers are required. Omitting one is a compile error, and there is
 * no `default` handler to absorb a variant, so a semantic distinction cannot be
 * quietly dropped here.
 */
export interface ObservationHandlers<TValue, TResult> {
  readonly onAvailable: (value: TValue, freshness: Freshness) => TResult;
  readonly onUnknown: (freshness: Freshness) => TResult;
  readonly onUnavailable: (freshness: Freshness) => TResult;
  readonly onFrontendCondition: (condition: FrontendCondition) => TResult;
}

/*
 * Variant discriminators.
 *
 * WHY GUARDS AND NOT A `switch` ON `presence`
 *   A branded value is an intersection of a string literal with an object type,
 *   and TypeScript does not treat it as a discriminant property: a `switch` on it
 *   narrows nothing, so every branch would silently see the whole union and the
 *   `default` branch would never be `never`. That was verified by typecheck, not
 *   assumed. The guards below narrow explicitly, and the final `assertNever` in
 *   `matchObservation` still proves exhaustiveness at compile time.
 *
 * WHY NOT A SEPARATE `kind` FIELD
 *   A second field carrying the same information as `presence` could drift from
 *   it. `presence` stays the single source of truth.
 *
 * `isAvailableObservation` IS NOT A SUCCESS PREDICATE. It identifies a variant.
 * It does not say the capability is available, ready, assigned or committed —
 * `AVAILABLE` means only that snapshot data was retrieved.
 */

export function isFrontendConditionObservation<TValue>(
  observation: ReadModelObservation<TValue>,
): observation is FrontendConditionObservation {
  return 'condition' in observation;
}

export function isAvailableObservation<TValue>(
  observation: ReadModelObservation<TValue>,
): observation is AvailableObservation<TValue> {
  return 'presence' in observation && observation.presence === Presence.AVAILABLE;
}

export function isUnknownObservation<TValue>(
  observation: ReadModelObservation<TValue>,
): observation is UnknownObservation {
  return 'presence' in observation && observation.presence === Presence.UNKNOWN;
}

export function isUnavailableObservation<TValue>(
  observation: ReadModelObservation<TValue>,
): observation is UnavailableObservation {
  return 'presence' in observation && observation.presence === Presence.UNAVAILABLE;
}

export function matchObservation<TValue, TResult>(
  observation: ReadModelObservation<TValue>,
  handlers: ObservationHandlers<TValue, TResult>,
): TResult {
  if (isFrontendConditionObservation(observation)) {
    return handlers.onFrontendCondition(observation.condition);
  }
  if (isAvailableObservation(observation)) {
    return handlers.onAvailable(observation.value, observation.freshness);
  }
  if (isUnknownObservation(observation)) {
    return handlers.onUnknown(observation.freshness);
  }
  if (isUnavailableObservation(observation)) {
    return handlers.onUnavailable(observation.freshness);
  }
  // `observation` is `never` here. Adding a variant without handling it is a
  // compile error at this line, which is the intended behaviour: the vocabulary
  // is frozen by the contract.
  return assertNever(observation, 'matchObservation');
}

/**
 * The subject's data was retrieved. NOT a claim of operational availability.
 */
export function observedAvailable<TValue>(
  value: TValue,
  freshness: Freshness,
): AvailableObservation<TValue> {
  return { presence: Presence.AVAILABLE, freshness, authority: NON_AUTHORITY, value };
}

/** The read plane resolved, and the subject's data is absent. NOT an error. */
export function observedUnknown(freshness: Freshness): UnknownObservation {
  return { presence: Presence.UNKNOWN, freshness, authority: NON_AUTHORITY, value: null };
}

/**
 * The reader could not supply a trustworthy response.
 * Carries no diagnostic: the frozen read seam publishes none, and neither does this.
 */
export function observedUnavailable(freshness: Freshness): UnavailableObservation {
  return { presence: Presence.UNAVAILABLE, freshness, authority: NON_AUTHORITY, value: null };
}

/** The frontend is in a condition that is not a contract outcome. */
export function observedFrontendCondition(
  condition: FrontendCondition,
): FrontendConditionObservation {
  return { condition, authority: NON_AUTHORITY };
}
